/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Frontend SPA Contract API
 * Canonical API contract entrypoint.
 *
 * Source architecture:
 * - split by module / bounded context
 * - one file per aggregate root
 * - child entities remain owned by aggregate root
 * - cross-cutting technical concerns live under `openapi/common`
 *
 * OpenAPI spec version: 1.0.0
 */
import type {
  CustomerOfferBindingDefinitionDto,
  ListOfferTemplatesParams,
  NoteId,
  NoteInput,
  OfferTemplateDto,
  OfferTemplateRequest,
  PaginatedOfferTemplateDto,
} from "../model";

import {customFetch} from "../../../runtime/custom-fetch";

export const getListOfferTemplatesUrl = (params?: ListOfferTemplatesParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/sales/offer-templates?${stringifiedParams}`
    : `/api/v1/sales/offer-templates`;
};

/**
 * Full DTOs with permitted source preview. Filters combine by AND; absent state includes ACTIVE and DELETED. Literal case-insensitive q searches name. Sort name ascending with ascending UUID tie-break. Protected live source calculation is skipped for unauthorized viewers.
 * @summary List reusable Offer Templates
 */
export const listOfferTemplates = async (
  params?: ListOfferTemplatesParams,
  options?: Parameters<typeof customFetch>[1],
): Promise<PaginatedOfferTemplateDto> => {
  return customFetch<PaginatedOfferTemplateDto>(getListOfferTemplatesUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getCreateOfferTemplateUrl = () => {
  return `/api/v1/sales/offer-templates`;
};

/**
 * Structurally valid content may have unresolved inputs. Authored Chapters explicitly carry applyCommission, initialized true for new authoring, and contain no coefficient or private pricing decision. Backend assigns identity; Notes initially empty. No Expedient, ordinal, version, predecessor, creator/approver override or calculated inputs. Protected financial source manipulation requires authorization. Every sold catalog item requires an actual description in the Template language, even with null quantity or empty supplier choices. Missing text returns sales.offerTemplate.invalidSelection with located SOURCE_MISSING and no Template creation; no fallback is permitted.
 * @summary Create reusable Template
 */
export const createOfferTemplate = async (
  offerTemplateRequest: OfferTemplateRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<string> => {
  return customFetch<string>(getCreateOfferTemplateUrl(), {
    ...options,
    method: "POST",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(offerTemplateRequest),
  });
};

export const getGetOfferTemplateUrl = (id: string) => {
  return `/api/v1/sales/offer-templates/${id}`;
};

/**
 * Authorized current source facts and contextual variable resolution; Template preview uses authenticated actor context. No arbitrary user lookup or automatic source propagation to previously created Offers.
 * @summary Get Template and permitted preview
 */
export const getOfferTemplate = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<OfferTemplateDto> => {
  return customFetch<OfferTemplateDto>(getGetOfferTemplateUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getUpdateOfferTemplateUrl = (id: string) => {
  return `/api/v1/sales/offer-templates/${id}`;
};

/**
 * ACTIVE only; complete name/language/currency/content replacement. Preserve identity/Notes and all independently instantiated Offers. Round-trip explicit applyCommission, including false, across unrelated edits/reorder/copy. Coefficients are forbidden in Template content and preview has only source costs, never commercial totals. Protected financial changes require permission, including Currency-only changes when catalog blocks reference protected sources. Currency changes without catalog content require only ordinary Template-update permission. New catalog blocks or changed sold identities/kinds require an actual description in the input language; missing text returns sales.offerTemplate.invalidSelection with located SOURCE_MISSING and preserves the complete original Template. Language changes retain existing selections; missing new-language descriptions produce located unavailable preview facts until description addition or eligible replacement. They cannot be copied into a new Customer Offer.
 * @summary Replace Template inputs
 */
export const updateOfferTemplate = async (
  id: string,
  offerTemplateRequest: OfferTemplateRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getUpdateOfferTemplateUrl(id), {
    ...options,
    method: "PUT",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(offerTemplateRequest),
  });
};

export const getDeleteOfferTemplateUrl = (id: string) => {
  return `/api/v1/sales/offer-templates/${id}`;
};

/**
 * ACTIVE only; existing Offers remain unchanged.
 * @summary Soft-delete Template
 */
export const deleteOfferTemplate = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getDeleteOfferTemplateUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getRestoreOfferTemplateUrl = (id: string) => {
  return `/api/v1/sales/offer-templates/${id}/restore`;
};

/**
 * DELETED to ACTIVE, same identity/content/Notes.
 * @summary Restore Template
 */
export const restoreOfferTemplate = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRestoreOfferTemplateUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getAddOfferTemplateNoteUrl = (id: string) => {
  return `/api/v1/sales/offer-templates/${id}/notes`;
};

/**
 * ACTIVE only; Template Notes are not copied into new Offers.
 * @summary Add Template Note
 */
export const addOfferTemplateNote = async (
  id: string,
  noteInput: NoteInput,
  options?: Parameters<typeof customFetch>[1],
): Promise<NoteId> => {
  return customFetch<NoteId>(getAddOfferTemplateNoteUrl(id), {
    ...options,
    method: "POST",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(noteInput),
  });
};

export const getEditOfferTemplateNoteUrl = (id: string, noteId: NoteId) => {
  return `/api/v1/sales/offer-templates/${id}/notes/${noteId}`;
};

/**
 * ACTIVE only; preserve Note identity.
 * @summary Replace Template Note
 */
export const editOfferTemplateNote = async (
  id: string,
  noteId: NoteId,
  noteInput: NoteInput,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getEditOfferTemplateNoteUrl(id, noteId), {
    ...options,
    method: "PUT",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(noteInput),
  });
};

export const getRemoveOfferTemplateNoteUrl = (id: string, noteId: NoteId) => {
  return `/api/v1/sales/offer-templates/${id}/notes/${noteId}`;
};

/**
 * ACTIVE only.
 * @summary Remove Template Note
 */
export const removeOfferTemplateNote = async (
  id: string,
  noteId: NoteId,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRemoveOfferTemplateNoteUrl(id, noteId), {
    ...options,
    method: "DELETE",
  });
};

export const getListOfferTemplateBindingDefinitionsUrl = () => {
  return `/api/v1/sales/offer-templates/binding-definitions`;
};

/**
 * Requires ordinary Resource read permission, not an existing Resource. Return all supported keys, including currently unresolvable keys, in stable order: $me.email then $technical_director. English labels and descriptions are metadata, not resolved values or availability claims. Email uses the authenticated Template preview user; opening rebinds to the new Offer creator. No arbitrary subject, expression or user-directory lookup is accepted. Existing evaluation reports unresolved values; literals stay literal and issued text remains frozen independently of discovery.
 * @summary Discover supported editor binding definitions
 */
export const listOfferTemplateBindingDefinitions = async (
  options?: Parameters<typeof customFetch>[1],
): Promise<CustomerOfferBindingDefinitionDto[]> => {
  return customFetch<CustomerOfferBindingDefinitionDto[]>(
    getListOfferTemplateBindingDefinitionsUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};
